import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PREVIEW MODE - Set to true for UI demo without backend
const PREVIEW_MODE = false;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('selena_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      // PREVIEW MODE - Auto-authenticate for demo
      if (PREVIEW_MODE && token) {
        const storedUser = localStorage.getItem('selena_user');
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        } else {
          setUser({ username: 'COMMANDER', role: 'admin' });
        }
        setLoading(false);
        return;
      }

      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('selena_token');
          localStorage.removeItem('selena_user');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (username, password) => {
    // PREVIEW MODE - Skip real auth
    if (PREVIEW_MODE) {
      const demoUser = { username: username || 'COMMANDER', role: 'admin' };
      localStorage.setItem('selena_token', 'PREVIEW_MODE_TOKEN');
      localStorage.setItem('selena_user', JSON.stringify(demoUser));
      setToken('PREVIEW_MODE_TOKEN');
      setUser(demoUser);
      return demoUser;
    }

    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('selena_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('selena_token');
    localStorage.removeItem('selena_user');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


